import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useT } from '@kmgestao/i18n';
import { Switch } from '@kmgestao/ui';
import { MoveHorizontal } from 'lucide-react';

import {
  ACTIVE_LEAD_STAGES,
  LEAD_STAGE_LABELS,
  TERMINAL_STAGES,
  formatPhoneBR,
  getLeadStageStyle,
  type Lead,
  type LeadStage,
} from '../../../features/leads/index.js';

/**
 * Kanban de leads — visão alternativa à lista densa do `LeadsListPage`.
 *
 * **Mobile-first de verdade** (regra §5.17 do AGENTS.md):
 *
 *   - **Sempre** `flex-row` + `overflow-x-auto` (mesmo padrão desktop):
 *     no mobile vira deslize lateral entre colunas (Linear/Trello-style),
 *     não empilhamento vertical degradado. Empilhar verticalmente
 *     transformava 9 stages em 9 seções sem contexto, exigindo scroll
 *     enorme passando por colunas vazias.
 *   - **Snap horizontal**: `snap-x snap-mandatory` no container +
 *     `snap-start` em cada coluna — deslize trava no início da
 *     próxima coluna, navegação previsível com 1 swipe = 1 coluna.
 *   - **Peek da próxima coluna**: largura da coluna ≈ 85vw no mobile
 *     (até max 20rem), deixando ~15% visível à direita como affordance
 *     "tem mais coluna aqui". Desktop fixa em `sm:w-72` (288px) onde
 *     várias colunas couberem juntas.
 *   - **Header sticky** dentro de cada coluna durante scroll vertical
 *     interno dos cards — não perde contexto "estou em proposta_aceita"
 *     ao rolar pra ver o lead 30.
 *   - **Toggle "esconder colunas vazias"**: no mobile faz diferença
 *     real — Karla com 8 colunas vazias e 1 com leads economiza scroll.
 *     Default: false (estado completo do funil é informativo).
 *   - **`max-h-[60dvh]`**: usa **dynamic viewport** (`dvh`) que respeita
 *     barra de URL do iOS Safari (encolhe ao scrollar). 60dvh ≈ 60% da
 *     altura útil — sobra espaço pra header da página + toolbar.
 *   - **Scroll hint**: texto "Deslize ←→ para navegar" só `< sm` pra
 *     guiar primeiro uso (afundance descoberta).
 *
 * Sem drag-and-drop nesta primeira entrega — transição de stage é
 * state-driven (webhook ou ações específicas). Arrastar pra "signed"
 * sem documento violaria truth-of-state. Adicionar com confirms se
 * virar pedido explícito.
 */
export interface LeadsKanbanBoardProps {
  leads: Lead[];
  /** Mostra coluna `lost`. Default: false (Karla foca em ativos). */
  includeLost?: boolean;
  /** Mostra coluna `provisioned`. Default: true (marco final). */
  includeProvisioned?: boolean;
}

export function LeadsKanbanBoard({
  leads,
  includeLost = false,
  includeProvisioned = true,
}: LeadsKanbanBoardProps) {
  const { t } = useT('clinics');
  const [hideEmpty, setHideEmpty] = useState(false);

  // Define quais colunas exibir, na ordem do funil. ACTIVE_LEAD_STAGES
  // já exclui provisioned/lost; adicionamos de volta conforme flags.
  const allColumns: LeadStage[] = [
    ...ACTIVE_LEAD_STAGES,
    ...(includeProvisioned ? (['provisioned'] as LeadStage[]) : []),
    ...(includeLost ? (['lost'] as LeadStage[]) : []),
  ];

  // Agrupa leads por stage uma vez só — evita filter quadrático.
  // Atribui o array antes do push pra que TS narrow corretamente em
  // modo build strict (`tsc -b`); set + push em duas linhas o
  // narrowing não persiste através do index access.
  const grouped = leads.reduce<Record<string, Lead[]>>((acc, lead) => {
    const bucket = acc[lead.stage] ?? [];
    bucket.push(lead);
    acc[lead.stage] = bucket;
    return acc;
  }, {});

  // Quando "Ocultar vazias" está ON, filtra colunas sem leads. Vale
  // pra mobile E desktop — Alexandre 2026-04-25 pediu que o toggle
  // funcione em ambos: faz sentido quando o funil tem leads
  // concentrados em poucas etapas (mais legibilidade, menos scroll
  // horizontal pra varrer).
  const visibleColumns = hideEmpty
    ? allColumns.filter((s) => (grouped[s]?.length ?? 0) > 0)
    : allColumns;

  return (
    <div className="space-y-2">
      {/* Toolbar: sempre visível. Mobile mostra hint de swipe à esquerda
          (gesture-affordance). Desktop esconde o hint (sem swipe lá) e
          o toggle fica alinhado à direita com `ml-auto`. Bar agrupado
          com bg-muted/40 pra peso visual e separação da lista. */}
      <div className="flex items-center justify-between gap-3 rounded-md border border-border bg-muted/40 px-3 py-2">
        <div className="flex items-center gap-1.5 text-sm text-muted-foreground sm:hidden">
          <MoveHorizontal className="size-4 shrink-0" aria-hidden="true" />
          <span>{t('kanban.mobile.swipeHint')}</span>
        </div>
        {/* `min-h-11` garante tap-target ≥ 44px do label inteiro (regra
            §Mobile-First). O Switch do DS por si só é h-6, então
            envolver num label com altura mínima estende a área tocável. */}
        <label className="ml-auto flex min-h-11 cursor-pointer items-center gap-2 text-sm font-medium text-foreground">
          <span>{t('kanban.toolbar.hideEmptyColumns')}</span>
          <Switch
            checked={hideEmpty}
            onCheckedChange={setHideEmpty}
            srLabel={t('kanban.toolbar.hideEmptyColumns')}
          />
        </label>
      </div>

      {/* Container do board: horizontal scroll com snap. No mobile o
          snap importa pra navegação previsível (1 swipe = 1 coluna);
          no desktop vira scroll livre (sm:snap-none) — quase sempre
          múltiplas colunas cabem juntas. Mesma lista visível em ambos
          (filtrada por hideEmpty quando aplicável). */}
      <div
        role="region"
        aria-label={t('kanban.ariaLabel')}
        className="-mx-2 flex snap-x snap-mandatory gap-3 overflow-x-auto px-2 pb-2 sm:-mx-0 sm:snap-none sm:px-0"
      >
        {visibleColumns.map((stage) => (
          <KanbanColumn key={stage} stage={stage} leads={grouped[stage] ?? []} />
        ))}
      </div>
    </div>
  );
}

/** Coluna do kanban — header sticky com contagem + lista de cards. */
function KanbanColumn({ stage, leads }: { stage: LeadStage; leads: Lead[] }) {
  const { t } = useT('clinics');
  const style = getLeadStageStyle(stage);
  const isMilestone = style.level === 'milestone';
  const isTerminal = TERMINAL_STAGES.includes(stage);

  return (
    <div
      className={`flex w-[85vw] max-w-[20rem] shrink-0 snap-start flex-col rounded-lg border border-border bg-card sm:w-72 sm:max-w-none sm:snap-none ${
        isMilestone ? 'border-accent/40' : ''
      }`}
    >
      {/* Header sticky pra preservar contexto durante scroll vertical
          interno dos cards. `top-0` cola no topo do scroller; `z-10`
          garante que fica sobre os cards. Destaque accent em
          colunas-marco. */}
      <header
        className={`sticky top-0 z-10 flex items-center justify-between gap-2 rounded-t-lg border-b border-border px-3 py-2 ${
          isMilestone ? 'bg-accent/10 backdrop-blur-sm' : 'bg-muted/80 backdrop-blur-sm'
        }`}
      >
        <div className="flex items-center gap-2">
          {style.icon ? <span aria-hidden="true">{style.icon}</span> : null}
          <h3 className="text-sm font-semibold text-foreground">{LEAD_STAGE_LABELS[stage]}</h3>
        </div>
        <span
          className="rounded-full bg-muted px-2 py-0.5 font-mono text-sm text-muted-foreground"
          aria-label={t('kanban.columnLabel', { count: leads.length })}
        >
          {leads.length}
        </span>
      </header>

      {/* Lista de cards — usa dynamic viewport height (`dvh`) que
          respeita a UI volátil do iOS Safari (barra de URL encolhe ao
          rolar). 60dvh sobra espaço pra header de página + toolbar. */}
      <ul className="max-h-[60dvh] space-y-2 overflow-y-auto p-2">
        {leads.length === 0 ? (
          <li className="px-2 py-4 text-center text-sm text-muted-foreground">
            {isTerminal ? t('kanban.emptyTerminal') : t('kanban.emptyDefault')}
          </li>
        ) : (
          leads.map((lead) => <LeadKanbanCard key={lead.id} lead={lead} />)
        )}
      </ul>
    </div>
  );
}

/** Card de lead no kanban — compacto pra caber colunas, mas com info
 * suficiente pra Karla decidir clicar (nome, contato, última atualização).
 *
 * Tap target: container inteiro do `<Link>`, altura mínima ≈ 80px com
 * 3 linhas de texto + paddings (≥ 44×44 conforme §5.17). */
function LeadKanbanCard({ lead }: { lead: Lead }) {
  const { t } = useT('clinics');
  const style = getLeadStageStyle(lead.stage);
  const display = lead.clinic_name_guess?.trim() || lead.full_name;

  return (
    <li>
      <Link
        to={`/superadmin/leads/${lead.id}`}
        className={`block rounded-md border border-border bg-background p-3 text-sm transition-colors hover:bg-muted/50 active:bg-muted/70 ${
          style.cardClassName ?? ''
        }`}
      >
        <p className="line-clamp-2 font-semibold text-foreground">{display}</p>
        {lead.clinic_name_guess && lead.clinic_name_guess !== lead.full_name ? (
          <p className="line-clamp-1 text-sm text-muted-foreground">{lead.full_name}</p>
        ) : null}
        <div className="mt-2 space-y-0.5 text-sm text-muted-foreground">
          {lead.email ? <p className="line-clamp-1 break-all">{lead.email}</p> : null}
          {lead.phone ? <p>{formatPhoneBR(lead.phone)}</p> : null}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          {t('kanban.card.updated', {
            relative: formatRelative(t, lead.updated_at ?? lead.created_at),
          })}
        </p>
      </Link>
    </li>
  );
}

/** Formata data ISO em "há 3 dias", "há 2h", "agora". Strings vêm do
 * i18n via parâmetro `t`. Sem libs externas (date-fns puxaria ~10KB). */
function formatRelative(t: ReturnType<typeof useT>['t'], iso: string | null | undefined): string {
  if (!iso) return '—';
  let date: Date;
  try {
    date = new Date(iso);
  } catch {
    return '—';
  }
  const diffMs = Date.now() - date.getTime();
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return t('kanban.relative.now');
  const min = Math.floor(sec / 60);
  if (min < 60) return t('kanban.relative.minutes', { value: min });
  const hr = Math.floor(min / 60);
  if (hr < 24) return t('kanban.relative.hours', { value: hr });
  const day = Math.floor(hr / 24);
  if (day < 30) return t('kanban.relative.days', { value: day, count: day });
  return date.toLocaleDateString('pt-BR');
}
